import React, { FC, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import type { CardOffer } from '../types/handTypes';
import CardDetail from './CardDetail';
import HandCard from './HandCard';
import { BRICK, CardSlot, Note as Pen, PlayButton, TextButton } from './deck';
import { DECK_SIZE, faceOf, HandCardDef, HandEntry } from './handModel';

/** The offered card's face, marked new. */
export function offerFace(offer: CardOffer): HandCardDef {
  return offer.definition
    ? { ...faceOf(offer.definition), isNew: true }
    : { slug: offer.cardId, emoji: '🃏', name: 'A card', rarity: 'common', text: '', when: 'now', isNew: true };
}

const SOURCE_LINE: Partial<Record<CardOffer['source'], string>> = {
  quiz: "from today's AOTD guessing",
  daily_drop: "from today's card drop",
  badge: 'a badge reward',
  milestone: 'a milestone reward',
  gift: 'a gift',
  event: 'from an event',
  admin: 'from the house',
};

/** Where the card came from: the server's reason, else its source in words. */
export function offerReason(offer: CardOffer): string | null {
  return offer.reason?.trim() || SOURCE_LINE[offer.source] || null;
}

const INCOMING = 'incoming';

interface OfferChooserProps {
  offer: CardOffer;
  /** the in_hand cards, oldest first */
  held: HandEntry[];
  full: boolean;
  wide: boolean;
  busy: boolean;
  /**
   * `inline`, in the hand: the incoming card sits at the end of the row and
   * its detail shows first. `modal`: the new card is read in full at the top
   * and the row holds only the cards you have.
   */
  layout: 'inline' | 'modal';
  /** a line under the new card's ability (the modal says when it can be played) */
  whenLine?: string;
  onKeep: (discardInstanceId?: string) => void;
  onThrow: () => void;
}

/**
 * Keep or throw a new card. With room in the deck it is "keep it" or "throw
 * it away" next to the card's ability. With a full deck you pick the card to
 * give up, read it, then confirm the swap; throwing the new card away is its
 * own small button. Tapping a card only ever opens it.
 */
const OfferChooser: FC<OfferChooserProps> = ({ offer, held, full, wide, busy, layout, whenLine, onKeep, onThrow }) => {
  const reduce = useReducedMotion();
  const face = offerFace(offer);
  const [pick, setPick] = useState<string | null>(layout === 'inline' ? INCOMING : null);
  const small = { w: wide ? 84 : 58, h: wide ? 118 : 82 };

  if (!full) {
    return (
      <CardDetail card={face} wide={wide} bare>
        {whenLine && (
          <Pen color={BRICK} size={15} style={{ marginBottom: 8 }}>
            {whenLine}
          </Pen>
        )}
        <Pen>there's room in your deck.</Pen>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginTop: 8 }}>
          <PlayButton type="button" disabled={busy} onClick={() => onKeep()}>
            keep it
          </PlayButton>
          <TextButton type="button" disabled={busy} onClick={onThrow}>
            throw it away
          </TextButton>
        </div>
      </CardDetail>
    );
  }

  const picked = pick && pick !== INCOMING ? held.find((e) => e.instanceId === pick) ?? null : null;
  const rowCard = (key: string, card: HandCardDef, label: string, extra?: string) => {
    const on = pick === key;
    return (
      <CardSlot
        key={key}
        type="button"
        onClick={() => setPick(key)}
        aria-pressed={on}
        aria-label={label}
        animate={reduce ? undefined : { y: on ? -6 : 0 }}
        whileHover={reduce || on ? undefined : { y: -4 }}
      >
        <HandCard card={card} w={small.w} h={small.h} note={extra} />
        {on && (
          <span
            aria-hidden="true"
            style={{ position: 'absolute', inset: -3, borderRadius: 9, border: `1.5px solid ${BRICK}`, pointerEvents: 'none' }}
          />
        )}
      </CardSlot>
    );
  };

  return (
    <div>
      {layout === 'modal' && (
        <div style={{ marginBottom: 14 }}>
          <CardDetail card={face} wide={wide} bare>
            {whenLine && (
              <Pen color={BRICK} size={15}>
                {whenLine}
              </Pen>
            )}
          </CardDetail>
        </div>
      )}
      <Pen>your deck holds {DECK_SIZE}. pick one to swap out.</Pen>
      <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginTop: 10, flexWrap: 'wrap' }}>
        {held.map((e) => rowCard(e.instanceId, e.card, `pick ${e.card.name} to swap out`))}
        {layout === 'inline' && rowCard(INCOMING, face, `read ${face.name}, the new card`, 'just arrived')}
      </div>

      {picked ? (
        <motion.div
          key={picked.instanceId}
          initial={reduce ? { opacity: 0 } : { opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          style={{ marginTop: 14 }}
        >
          <CardDetail card={picked.card} wide={wide} bare>
            <Pen>
              swap {picked.card.emoji} {picked.card.name} for {face.emoji} {face.name}?
            </Pen>
            <div style={{ marginTop: 8 }}>
              <PlayButton type="button" disabled={busy} onClick={() => onKeep(picked.instanceId)}>
                swap
              </PlayButton>
            </div>
          </CardDetail>
        </motion.div>
      ) : pick === INCOMING ? (
        <motion.div
          key={INCOMING}
          initial={reduce ? { opacity: 0 } : { opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          style={{ marginTop: 14 }}
        >
          <CardDetail card={face} wide={wide} bare>
            <Pen>to keep it, pick one of your cards to swap out.</Pen>
          </CardDetail>
        </motion.div>
      ) : null}

      <div style={{ textAlign: 'center', marginTop: 12 }}>
        <TextButton type="button" disabled={busy} onClick={onThrow}>
          throw the new one away
        </TextButton>
      </div>
    </div>
  );
};

export default OfferChooser;
